import { bool, func, number, oneOfType, string } from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Typography from 'ravenjs/lib/Typography';
import { callFunc } from 'ravenjs/utils/actions';
import Box from 'ravenjs/lib/Box';
import ButtonLink from 'ravenjs/lib/Button/ButtonLink';

import { ThrashIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { EVENTS } from 'constants/events';
import MESSAGES from 'constants/messages';
import { actions as pendoActions } from 'modules/pendo';
import { getSimpleFormattedTimestamp } from 'utils/date';
import { STATUS } from 'utils/notifications';

const NotificationItemStyledV1 = styled(Box)`
    background: ${({ zebra }) => (zebra ? DEFAULT_COLORS.TABLE_GREY : DEFAULT_COLORS.WHITE)};
    border: none;
    border-radius: 0;
    cursor: pointer;
    margin: 0;
    padding: 0;
    &:hover {
        background: ${({ type }) =>
            type === 'notification-row' ? DEFAULT_COLORS.BLUE : 'inherit'};
    }
`;

const NotificationItemStyledV2 = styled(Box)`
    background: ${({ zebra }) => (zebra ? DEFAULT_COLORS.TABLE_GREY : DEFAULT_COLORS.WHITE)};
    border: none;
    border-radius: 0;
    cursor: pointer;
    margin: 0;
    padding: 0;
    &:hover {
        background: ${({ type }) =>
            type === 'notification-row' ? DEFAULT_COLORS.BLUE : 'inherit'};
    }
    /* V2 keyboard navigation focus styles */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: -2px;
    }
`;

const ConfirmationButtonCommon = styled(Box)`
    cursor: pointer;
    width: 3.5rem;
    padding: 0;
    border-radius: 4px;
    text-align: center;
    border-color: ${DEFAULT_COLORS.DARK};
    color: ${DEFAULT_COLORS.BLACK};
    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE};
        color: ${DEFAULT_COLORS.WHITE};
    }
`;

const ConfirmationButtonV1 = styled(ConfirmationButtonCommon)``;

const ConfirmationButtonV2 = styled(ConfirmationButtonCommon)`
    /* V2 keyboard navigation focus styles */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const DeleteIconWrapper = styled.span`
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    /* V2 keyboard navigation focus styles */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    margin-left: auto;
`;

const NotificationItemBody = styled.div`
    padding: 0.688rem 1.438rem 0.688rem 0.563rem;
    display: flex;
    align-items: center;
`;

function NotificationItem({
    entityType,
    id,
    isNavigationV2Enabled,
    onClick,
    onDelete,
    onViewMore,
    sendPendoEvent,
    status,
    summary,
    timestamp,
    zebra,
    onFocusNextItem,
}) {
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessDeletionMsg, setShowSuccessDeletionMsg] = useState(false);

    const [isNotificationDeleted, setisNotificationDeleted] = useState(false);

    const NotificationItemStyled = isNavigationV2Enabled
        ? NotificationItemStyledV2
        : NotificationItemStyledV1;

    const ConfirmationButton = isNavigationV2Enabled ? ConfirmationButtonV2 : ConfirmationButtonV1;

    function trackEvent(event) {
        const pendoEvent = event;
        pendoEvent.eventProperties.entityId = id;
        pendoEvent.eventProperties.entityType = entityType;
        sendPendoEvent(pendoEvent);
    }

    function handleOnClickDelete(e) {
        e.stopPropagation();
        trackEvent(EVENTS.NOTIFICATION.BELL_ICON_DELETE_ENTITY_SELECT);
        setShowDeleteConfirmation(true);
    }

    function handleDeleteKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleOnClickDelete(e);
        }
    }

    function handleNoKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            closeDeleteConfirmationMessage(e);
        }
    }

    function handleYesKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleNotificationDelete(e);
        }
    }

    function handleOnClick(e) {
        // Preventing from onClickNotificationIcon event trigger
        e.stopPropagation();
        callFunc(onClick, id);
    }

    function closeDeleteConfirmationMessage(e) {
        if (e) {
            e.stopPropagation();
        }
        trackEvent(EVENTS.NOTIFICATION.BELL_ICON_DELETE_ENTITY_CANCEL);
        setShowDeleteConfirmation(false);

        // Return focus to the notification item row only if triggered by keyboard
        // Check (keyboard event) vs mouse click
        if (isNavigationV2Enabled && e && (e.key === 'Enter' || e.key === ' ')) {
            setTimeout(() => {
                const notificationRow = document.querySelector(`[data-notification-id="${id}"]`);
                if (notificationRow) {
                    notificationRow.focus();
                }
            }, 50);
        }
    }

    function handleNotificationDelete(e) {
        e.stopPropagation();
        trackEvent(EVENTS.NOTIFICATION.BELL_ICON_DELETE_ENTITY_CONFIRM);
        setShowSuccessDeletionMsg(true);
        setShowDeleteConfirmation(false);
        setisNotificationDeleted(true);
        callFunc(onDelete, id);

        // After deletion, focus should move to the next item only if triggered by keyboard
        // Check (keyboard event) vs mouse click
        if (isNavigationV2Enabled && onFocusNextItem && e && (e.key === 'Enter' || e.key === ' ')) {
            setTimeout(() => {
                onFocusNextItem();
            }, 100);
        }
    }

    return (
        <>
            {!isNotificationDeleted && (
                <NotificationItemStyled
                    gutter={false}
                    onClick={handleOnClick}
                    type="notification-row"
                    zebra={zebra}
                    // V2 keyboard navigation attributes
                    data-notification-item={isNavigationV2Enabled ? 'true' : undefined}
                    data-notification-id={isNavigationV2Enabled ? id : undefined}
                    tabIndex={isNavigationV2Enabled ? 0 : undefined}
                >
                    <NotificationItemBody>
                        <Typography
                            status={status}
                            color={DEFAULT_COLORS.BLACK}
                            gutterBottom="0"
                            gutterTop="0"
                            type="notificationItemMessage"
                            lineHeight="1.33"
                            fontFamily="Roboto"
                            fontWeight={status === STATUS.READ ? '300' : '500'}
                            padding="0 1rem 0 0"
                        >
                            {summary}
                        </Typography>
                        <Typography
                            color={DEFAULT_COLORS.BLACK}
                            fontFamily="Roboto"
                            fontWeight={status === STATUS.READ ? '300' : '500'}
                            gutterLeft="auto"
                            gutterRight="1.5rem"
                            gutterBottom="0"
                            gutterTop="0"
                            type="notificationItemMessage"
                        >
                            {getSimpleFormattedTimestamp(timestamp)}
                        </Typography>
                        {isNavigationV2Enabled ? (
                            <DeleteIconWrapper
                                tabIndex={0}
                                onClick={handleOnClickDelete}
                                onKeyDown={handleDeleteKeyDown}
                                role="button"
                                aria-label="Delete notification"
                            >
                                <ThrashIcon />
                            </DeleteIconWrapper>
                        ) : (
                            <Typography
                                gutterBottom="0"
                                gutterTop="0"
                                type="link"
                                onClick={handleOnClickDelete}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <ThrashIcon />
                            </Typography>
                        )}
                    </NotificationItemBody>
                </NotificationItemStyled>
            )}

            {showDeleteConfirmation && (
                <NotificationItemStyled gutter={false}>
                    <NotificationItemBody>
                        <Typography
                            color={DEFAULT_COLORS.BLACK}
                            gutterTop="0"
                            gutterLeft="0.313rem"
                            justify="center"
                            fullWidth
                            gutterBottom="0"
                            fontWeight="300"
                            lineHeight="1.5"
                            type="notificationItemMessage"
                        >
                            {MESSAGES.NOTIFICATIONS.CONFIRMATION_MESSAGE}
                        </Typography>

                        <ActionButtons>
                            <ConfirmationButton
                                margin="0"
                                onClick={closeDeleteConfirmationMessage}
                                tabIndex={isNavigationV2Enabled ? 0 : undefined}
                                onKeyDown={isNavigationV2Enabled ? handleNoKeyDown : undefined}
                                role={isNavigationV2Enabled ? 'button' : undefined}
                                aria-label={isNavigationV2Enabled ? 'Cancel delete' : undefined}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <Typography
                                    color={DEFAULT_COLORS.BLACK}
                                    gutterBottom="0"
                                    type="notificationItemMessage"
                                >
                                    {MESSAGES.NOTIFICATIONS.NO}
                                </Typography>
                            </ConfirmationButton>
                            <ConfirmationButton
                                onClick={handleNotificationDelete}
                                margin="0 0 0 0.1888rem"
                                tabIndex={isNavigationV2Enabled ? 0 : undefined}
                                onKeyDown={isNavigationV2Enabled ? handleYesKeyDown : undefined}
                                role={isNavigationV2Enabled ? 'button' : undefined}
                                aria-label={isNavigationV2Enabled ? 'Confirm delete' : undefined}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <Typography
                                    color={DEFAULT_COLORS.BLACK}
                                    gutterBottom="0"
                                    type="notificationItemMessage"
                                >
                                    {MESSAGES.NOTIFICATIONS.YES}
                                </Typography>
                            </ConfirmationButton>
                        </ActionButtons>
                    </NotificationItemBody>
                </NotificationItemStyled>
            )}
            {showSuccessDeletionMsg && (
                <NotificationItemStyled gutter={false}>
                    <NotificationItemBody>
                        <Typography
                            color={DEFAULT_COLORS.BLACK}
                            gutterTop="0"
                            gutterLeft="0.313rem"
                            justify="center"
                            fullWidth
                            gutterBottom="0"
                            fontWeight="300"
                            lineHeight="1.5"
                            type="notificationItemMessage"
                        >
                            {MESSAGES.NOTIFICATIONS.DELETED}
                        </Typography>
                        <Typography
                            color={DEFAULT_COLORS.BLACK}
                            gutterTop="0"
                            gutterLeft="0.313rem"
                            justify="center"
                            fullWidth
                            gutterBottom="0"
                            fontWeight="300"
                            lineHeight="1.5"
                            type="notificationItemMessage"
                        >
                            {MESSAGES.NOTIFICATIONS.ALL_NOTIFICATIONS}
                            <ButtonLink onClick={onViewMore}>here.</ButtonLink>
                        </Typography>
                    </NotificationItemBody>
                </NotificationItemStyled>
            )}
        </>
    );
}

NotificationItem.propTypes = {
    entityType: string,
    id: oneOfType([string, number]),
    isNavigationV2Enabled: bool,
    onClick: func.isRequired,
    onViewMore: func.isRequired,
    onDelete: func.isRequired,
    summary: string,
    timestamp: number,
    sendPendoEvent: func.isRequired,
    status: string,
    zebra: bool,
    onFocusNextItem: func,
};

NotificationItem.defaultProps = {
    entityType: '',
    id: null,
    isNavigationV2Enabled: false,
    summary: null,
    timestamp: null,
    status: null,
    zebra: false,
    onFocusNextItem: null,
};

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
};

export { NotificationItem as NotificationItemUnwrapped };
export default connect(null, mapDispatchToProps)(NotificationItem);
